import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getAllWithdrawals, approveWithdrawal, rejectWithdrawal, toMoney } from "@/services/supabaseService";
import type { Withdrawal } from "@/lib/supabase";
import AdminLayout from "@/components/feature/AdminLayout";

export default function WithdrawalAudit() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"" | "success" | "error">("");
  const [filter, setFilter] = useState<"all" | "pending" | "completed" | "rejected">("pending");

  const loadData = async () => {
    setIsLoading(true);
    try {
      const data = await getAllWithdrawals();
      setWithdrawals(data);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-forbid-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">權限不足</p>
          <p className="text-sm text-gray-400 mb-6">只有管理員才能訪問此頁面</p>
          <button onClick={() => navigate("/")} className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer">
            返回首頁
          </button>
        </div>
      </div>
    );
  }

  const filtered = filter === "all"
    ? withdrawals
    : withdrawals.filter((w) =>
        filter === "pending"
          ? w.status === "待審核"
          : filter === "completed"
          ? w.status === "已完成"
          : w.status === "已退回"
      );

  const pendingCount = withdrawals.filter((w) => w.status === "待審核").length;
  const completedCount = withdrawals.filter((w) => w.status === "已完成").length;
  const rejectedCount = withdrawals.filter((w) => w.status === "已退回").length;

  const handleApprove = async (id: string) => {
    try {
      await approveWithdrawal(id);
      setMsg(`提現 ${id} 已核准完成`);
      setMsgType("success");
      await loadData();
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : "操作失敗");
      setMsgType("error");
    }
    setTimeout(() => setMsg(""), 4000);
  };

  const handleReject = async () => {
    if (!rejectId || !rejectNote.trim()) return;
    try {
      await rejectWithdrawal(rejectId, rejectNote.trim());
      setMsg(`提現 ${rejectId} 已退回，金額已返還用戶錢包`);
      setMsgType("success");
      await loadData();
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : "操作失敗");
      setMsgType("error");
    }
    setRejectId(null);
    setRejectNote("");
    setTimeout(() => setMsg(""), 4000);
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <button onClick={() => navigate("/admin/dashboard")} className="hover:text-amber-600 cursor-pointer">
            管理後台
          </button>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-xs"></i>
          </div>
          <span className="text-gray-700">提現審核</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "全部提現", value: withdrawals.length, color: "bg-gray-100 text-gray-700", icon: "ri-file-list-3-line" },
            { label: "待審核", value: pendingCount, color: "bg-amber-100 text-amber-700", icon: "ri-time-line" },
            { label: "已完成", value: completedCount, color: "bg-emerald-100 text-emerald-700", icon: "ri-check-line" },
            { label: "已退回", value: rejectedCount, color: "bg-red-100 text-red-700", icon: "ri-close-line" },
          ].map((s) => (
            <div key={s.label} className={`rounded-xl p-4 text-center shadow-sm ${s.color}`}>
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <i className={`${s.icon} text-xs`}></i>
                <p className="text-xs font-medium">{s.label}</p>
              </div>
              <p className="text-xl font-bold">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 inline-flex">
          {([
            { key: "pending" as const, label: "待審核" },
            { key: "all" as const, label: "全部" },
            { key: "completed" as const, label: "已完成" },
            { key: "rejected" as const, label: "已退回" },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                filter === tab.key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Message */}
        {msg && (
          <div className={`rounded-lg px-4 py-3 text-sm border mb-1 flex items-center gap-2 shadow-sm ${
            msgType === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-600"
              : "bg-red-50 border-red-200 text-red-600"
          }`}>
            <div className="w-4 h-4 flex items-center justify-center shrink-0">
              <i className={`${msgType === "success" ? "ri-check-line" : "ri-error-warning-line"} text-xs`}></i>
            </div>
            {msg}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="px-6 py-12 text-center text-gray-400 text-sm">載入中...</div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-400 text-sm">暫無提現申請</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-50">
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">申請編號</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">用戶</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">金額</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">銀行資訊</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">申請時間</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">狀態</th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-400">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((w) => (
                    <tr key={w.id} className="hover:bg-gray-50/50">
                      <td className="px-5 py-4">
                        <p className="text-sm font-medium text-gray-800">{w.id}</p>
                        {w.admin_note && <p className="text-xs text-red-500 mt-0.5">備註：{w.admin_note}</p>}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">{w.user_name}</td>
                      <td className="px-5 py-4 text-sm font-medium text-gray-900">${toMoney(w.amount).toLocaleString()}</td>
                      <td className="px-5 py-4 text-xs text-gray-500 leading-relaxed">
                        {w.bank_name} / {w.bank_branch}<br />
                        {w.bank_account} ({w.account_holder})
                      </td>
                      <td className="px-5 py-4 text-xs text-gray-400">
                        {new Date(w.created_at).toLocaleString("zh-TW")}
                        {w.reviewed_at && <p className="text-emerald-600 mt-0.5">審核 {new Date(w.reviewed_at).toLocaleString("zh-TW")}</p>}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          w.status === "待審核"
                            ? "bg-amber-100 text-amber-700"
                            : w.status === "已完成"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-600"
                        }`}>
                          {w.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {w.status === "待審核" ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleApprove(w.id)}
                              className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 cursor-pointer whitespace-nowrap"
                            >
                              核准
                            </button>
                            <button
                              onClick={() => { setRejectId(w.id); setRejectNote(""); setMsg(""); }}
                              className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 cursor-pointer whitespace-nowrap"
                            >
                              拒絕
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">已處理</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-base font-semibold text-gray-900 mb-1">拒絕提現申請</h3>
            <p className="text-sm text-gray-500 mb-4">申請編號：{rejectId}</p>
            <p className="text-sm text-gray-500 mb-4">拒絕後系統將自動將金額退回用戶錢包。請輸入原因：</p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="例如：銀行帳號有誤、資訊不完整..."
              maxLength={200}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 mb-4 resize-none"
            />
            <p className="text-xs text-gray-400 mb-4 text-right">{rejectNote.length}/200</p>
            <div className="flex gap-2">
              <button
                onClick={handleReject}
                disabled={!rejectNote.trim()}
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                確認拒絕並退款
              </button>
              <button
                onClick={() => { setRejectId(null); setRejectNote(""); }}
                className="flex-1 py-2.5 rounded-lg bg-gray-100 text-gray-600 text-sm hover:bg-gray-200 cursor-pointer"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}